import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import mqttService from "../services/mqttService";

interface MQTTContextType {
  isConnected: boolean;
  reconnect: () => Promise<void>;
  disconnect: () => void;
  subscribe: (topic: string, callback: (topic: string, message: string) => void) => void;
  publish: (topic: string, message: string, options?: any) => void;
}

const MQTTContext = createContext<MQTTContextType | undefined>(undefined);

export const MQTTProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(mqttService.isConnected());

  const reconnect = useCallback(async () => {
    try {
      await mqttService.connect();
      setIsConnected(true);
    } catch (err) {
      setIsConnected(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    mqttService.disconnect();
    setIsConnected(false);
  }, []);

  const subscribe = useCallback((topic: string, callback: (topic: string, message: string) => void) => {
    mqttService.subscribe(topic, callback);
  }, []);

  const publish = useCallback((topic: string, message: string, options?: any) => {
    mqttService.publish(topic, message, options);
  }, []);

  useEffect(() => {
    // Check authentication before connecting
    const userData = localStorage.getItem('userData');
    const isAuthenticated = userData && JSON.parse(userData)?.isAuthenticated;
    if (isAuthenticated) {
      reconnect();
    } else {
      mqttService.disconnect();
    }

    // Listen for connection events
    const client = (mqttService as any).client;
    if (client) {
      const handleConnect = () => setIsConnected(true);
      const handleClose = () => setIsConnected(false);
      const handleError = () => setIsConnected(false);
      client.on("connect", handleConnect);
      client.on("close", handleClose);
      client.on("error", handleError);
      return () => {
        client.removeListener("connect", handleConnect);
        client.removeListener("close", handleClose);
        client.removeListener("error", handleError);
      };
    }
  }, [reconnect]);

  return (
    <MQTTContext.Provider value={{ isConnected, reconnect, disconnect, subscribe, publish }}>
      {children}
    </MQTTContext.Provider>
  );
};

export function useMQTT() {
  const context = useContext(MQTTContext);
  if (!context) {
    throw new Error("useMQTT must be used within an MQTTProvider");
  }
  return context;
} 